import { ToastContainer } from 'react-toastify';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Cookies from 'universal-cookie';

import InputField from 'components/fields/InputField';
import CommandInfo from './components/ProductInfo';
import CreateButton from 'components/buttons/CreateButton';
import CatalogInterestModal from './components/CatalogInterestModal';
import {
    loginOrder,
    pingAutoOrderPage,
    resolveSupplier,
    verifyLoginOrder,
} from 'interfaces/autoOrder';
import { getLocalOrCookie } from 'utils/localAndCookieStorage';

const cookies = new Cookies(null, { path: '/' });

const CommandPage = () => {
    const params = useParams();
    const [isAuth, setIsAuth] = useState(false);
    const [email, setEmail] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [haveMultipleSupliers, setHaveMultipleSupliers] = useState(false);
    const [isSelfPreview, setIsSelfPreview] = useState(false);
    const [showInterestModal, setShowInterestModal] = useState(false);
    const [supplierId, setSupplierId] = useState<string | null>(
        () => new URLSearchParams(window.location.search).get('supplier')
    );

    const login = async () => {
        const res = await loginOrder({ email, supplier: supplierId });
        if (res) {
            setEmailSent(true);
        }
    };

    // When the URL carries no ?supplier= (a company's own "Commander" button
    // just links to the generic shop.toola.fr/command), try to resolve which
    // company this is for: first via the personalized /c/:slug link, then by
    // matching the referring site's hostname against each company's website.
    useEffect(() => {
        if (supplierId) return;

        const resolve = async () => {
            if (params.slug) {
                const res = await resolveSupplier({ slug: params.slug });
                if (res?.supplier) setSupplierId(res.supplier);
                return;
            }

            if (document.referrer) {
                try {
                    const referrerHost = new URL(document.referrer).hostname;
                    if (referrerHost && referrerHost !== window.location.hostname) {
                        const res = await resolveSupplier({ origin: referrerHost });
                        if (res?.supplier) setSupplierId(res.supplier);
                    }
                } catch {
                    // Malformed/opaque referrer - nothing we can do.
                }
            }
        };

        resolve();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.slug]);

    const verifyAuth = async (v: string) => {
        const res = await verifyLoginOrder({ magicLink: v });
        if (res) {
            if (res.suppliers.length > 1) setHaveMultipleSupliers(true);
            setIsSelfPreview(!!res.isSelfPreview);
            // Save the token in both storage locations with a long duration.
            localStorage.setItem('order-token', res.token);
            cookies.set('order-token', res.token, {
                maxAge: 365 * 24 * 60 * 60, // 1 year
                sameSite: 'lax'
            });
            setIsAuth(true);
            setIsLoaded(true);
        } else {
            const storedToken = getLocalOrCookie('order-token');
            if (storedToken) {
                await redirectIfAuth();
            } else {
                setIsLoaded(true);
            }
        }
    };

    const redirectIfAuth = async () => {
        const token = getLocalOrCookie('order-token');
        if (!token) {
            setIsAuth(false);
            setIsLoaded(true);
            return;
        }

        const res = await pingAutoOrderPage();
        if (res) {
            // Re-save the token to extend the session
            localStorage.setItem('order-token', token);
            cookies.set('order-token', token, { 
                maxAge: 365 * 24 * 60 * 60,
                sameSite: 'lax'
            });
            if (res.suppliers?.length > 1) setHaveMultipleSupliers(true);
            setIsSelfPreview(!!res.isSelfPreview);
            setIsAuth(true);
            setIsLoaded(true);
        } else {
            setIsAuth(false);
            localStorage.removeItem('order-token');
            cookies.remove('order-token');
            setIsLoaded(true);
        }
    };

    useEffect(() => {
        // Wait a short while to ensure that localStorage is accessible
        const initAuth = async () => {
            // First, check the magic link.
            if (params.v) {
                await verifyAuth(params.v);
                return;
            }

            // Otherwise, check the stored token.
            const storedToken = getLocalOrCookie('order-token');
            if (storedToken) {
                await redirectIfAuth();
            } else {
                setIsLoaded(true);
            }
        };

        // Slight delay for iOS/PWA
        const timer = setTimeout(() => {
            initAuth();
        }, 100);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.v]);

    return (
        <div className="bg-[#f7f6f7]">
            <ToastContainer />
            {isAuth && isLoaded && (
                <div className="border-5 shadow-3x grid h-full w-full grid-cols-12 content-center items-center gap-3 rounded-[20px] bg-[#f7f6f7] bg-clip-border p-[10px] py-0 align-middle">
                    <div className="col-span-12 items-center">
                        <CommandInfo
                            haveMultipleSupliers={haveMultipleSupliers}
                            isSelfPreview={isSelfPreview}
                        />
                    </div>
                </div>
            )}
            {!isAuth && isLoaded && (
                <div className="mb-16 flex h-full w-full items-center justify-center">
                    {/* Sign in section */}
                    <div className="mt-[16vh] flex max-w-[90%] flex-col rounded-[20px] pb-16 pt-12 lg:max-w-[42%]">
                        {!emailSent ? (
                            <>
                                <h2 className="mb-2 text-4xl font-bold text-navy-700 dark:text-white">
                                    Bienvenue sur Toola 👋
                                </h2>
                                <p className="mb-8 ml-1 mt-[10px] text-base text-gray-600">
                                    Connectez-vous à l'espace de commande de
                                    votre fournisseur.
                                </p>
                                <InputField
                                    variant="auth"
                                    label="Adresse mail"
                                    placeholder="marc@votre-entrepise.com"
                                    id="email"
                                    type="text"
                                    onChange={(e: any) =>
                                        setEmail(e.target.value)
                                    }
                                />
                                {/* button */}
                                <CreateButton
                                    extra="mt-5"
                                    value="Recevoir l'accès par mail"
                                    onClick={login}
                                />
                                {supplierId && (
                                    <button
                                        onClick={() =>
                                            setShowInterestModal(true)
                                        }
                                        className="linear mt-3 flex items-center justify-center rounded-xl border border-[#0260CB] px-4 py-3 text-base font-medium text-[#0260CB] hover:bg-blue-50"
                                    >
                                        Intéressé(e) par le catalogue ?
                                    </button>
                                )}
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center text-center">
                                <p className="mt-10 text-[100px] text-[#0260CB]">
                                    ✉️
                                </p>
                                <p className="text-xl font-bold text-navy-700">
                                    Un email vous a été envoyé
                                </p>
                                <p className="mt-2 text-base text-gray-600">
                                    Consultez votre boite mail pour accéder à
                                    votre espace de commande
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {supplierId && (
                <CatalogInterestModal
                    isOpen={showInterestModal}
                    onClose={() => setShowInterestModal(false)}
                    supplier={supplierId}
                />
            )}
        </div>
    );
};

export default CommandPage;
